import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useFavorites } from '../hooks/useFavorites';
import { getChildCategoryName } from '../utils/categoryPath';
import { isUuid, setJsonLd, setPageSeo, crafterUrl as marketplaceCrafterUrl } from '../utils/urls';
import { useShop } from '../shop/ShopContext';
import ShopProductExtras from '../shop/ShopProductExtras';
import { exitShop } from '../lib/shopSession';
import { optimizeImageUrl } from '../utils/imageUrl';
import { vacationStatus, formatDateIt } from '../utils/vacation';
import { useTracking } from '../hooks/useTracking';
import { RecommendedProducts } from '../components/RecommendedProducts';
import { CrafterStripeStatusBanner } from '../components/CrafterStripeStatusBanner';
import { useCrafterCanSell } from '../hooks/useCrafterCanSell';
import { AddedToCartDialog } from '../components/AddedToCartDialog';
import { calculateFinalPrice, calculateFullPrice, isLaunchModeActive, getTotalDiscountPercentage, getFeeBreakdown } from '../lib/feeCalculator';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft,
  ChevronRight,
  Clock,
  Package,
  MessageSquare,
  Heart,
  Share2,
  Check,
  Star,
  Shield,
  Truck,
  RefreshCw,
  Upload,
  X,
  Image as ImageIcon,
  AlertCircle,
  MapPin,
  Award,
  ZoomIn,
  ShoppingCart,
  Sparkles,
  TreeDeciduous,
  Leaf,
  Gift,
  Hand,
  Fingerprint,
  PenTool,
  History,
  Paintbrush,
  Scissors,
  Hammer,
  Gem,
  Crown,
  Circle,
  Mountain,
  Coffee,
  Recycle,
  Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { ReviewStars, ReviewSummary, ReviewsList } from '../components/ReviewsList';

const TIMELINE_LABELS = {
  'immediate': 'Disponibilità immediata',
  '1-2days': '1-2 giorni',
  '3-5days': '3-5 giorni',
  '1week': '1 settimana',
  '1-2weeks': '1-2 settimane',
  '3-4weeks': '3-4 settimane',
  'over-month': 'Oltre un mese',
};

export default function ProductDetailPage() {
  const params = useParams();
  const routeKey = params.slug || params.id;
  // Dentro il negozio personale: link e percorso restano nel negozio
  const shop = useShop();
  const crafterUrl = (c) => (shop ? shop.paths.home : marketplaceCrafterUrl(c));
  const productPath = (p) => (shop ? shop.paths.product(p) : `/prodotto/${p.slug || p.id}`);
  const [id, setId] = useState(isUuid(routeKey) ? routeKey : null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isFavorite: checkIsFavorite, toggleFavorite } = useFavorites();
  const { trackProductView, trackFavorite } = useTracking();
  const fileInputRef = useRef(null);
  
  const [product, setProduct] = useState(null);
  const [crafter, setCrafter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedCustomizations, setSelectedCustomizations] = useState({});
  const [customizationValues, setCustomizationValues] = useState({}); // For text/select inputs
  const [selectedVariants, setSelectedVariants] = useState({}); // For variant selections
  const [customerAttachments, setCustomerAttachments] = useState({});
  const [addedDialog, setAddedDialog] = useState({ open: false, item: null, cartCount: 0 });
  const [uploadingAttachment, setUploadingAttachment] = useState(null);
  const [uploadErrors, setUploadErrors] = useState({}); // errore persistente per personalizzazione
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [reviewStats, setReviewStats] = useState({ count: 0, average: 0 });
  const [crafterRating, setCrafterRating] = useState({ count: 0, average: 0 });
  const [shippingInfo, setShippingInfo] = useState(null);
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [productHighlights, setProductHighlights] = useState([]);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Verifica se l'artigiano può ricevere ordini (Stripe onboarding completato)
  const { canSell: crafterCanSell, loading: canSellLoading } = useCrafterCanSell(
    product?.crafter_id
  );
  
  // Icon mapping for highlights
  const getHighlightIcon = (iconName) => {
    const icons = {
      TreeDeciduous, Leaf, Shield, Gift, Hand, Fingerprint, PenTool, 
      History, Paintbrush, Scissors, Hammer, Gem, Crown, Circle, Mountain, 
      Coffee, Award, Heart, Recycle, MapPin, Layers, Star, Sparkles, Package,
      Truck, Clock, Check
    };
    return icons[iconName] || Star;
  };

  // Check if product is favorite
  const isFavorite = product ? checkIsFavorite(product.id, 'product') : false;
  
  const handleToggleFavorite = async () => {
    if (product) {
      await toggleFavorite(product.id, 'product');
      // Track favorite action
      if (!isFavorite) {
        trackFavorite(product.id, product.crafter_id);
      }
    }
  };

  // Track product view when product loads
  useEffect(() => {
    if (product && crafter) {
      const categorySlug = product.customization_options?.categoryPath?.main || product.category;
      const cleanup = trackProductView(product.id, crafter.id, categorySlug);
      return cleanup; // Track duration on unmount
    }
  }, [product, crafter, trackProductView]);

  // SEO: title, description, canonical + redirect verso URL con slug
  useEffect(() => {
    if (!product) return;
    const shopName = crafter?.storefront_title || crafter?.full_name || 'Rarifex';
    const mainImage = product.images?.[product.customization_options?.main_image_index || 0] || product.images?.[0];
    setPageSeo({
      title: `${product.title} | ${shopName} · Rarifex`,
      description: product.description || `${product.title} artigianale Made in Italy su Rarifex.`,
      canonicalPath: `/prodotto/${product.slug || product.id}`,
      image: mainImage,
    });
    // Dati strutturati del prodotto (prezzo finale come mostrato in pagina)
    setJsonLd('product', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.title,
      ...(product.description ? { description: product.description.slice(0, 500) } : {}),
      ...(product.images?.length ? { image: product.images.slice(0, 4) } : {}),
      brand: { '@type': 'Brand', name: shopName },
      offers: {
        '@type': 'Offer',
        url: `${window.location.origin}/prodotto/${product.slug || product.id}`,
        priceCurrency: 'EUR',
        price: calculateFinalPrice(product.base_price || 0, product.customization_options?.crafterDiscount || 0).toFixed(2),
        seller: { '@type': 'Organization', name: shopName },
      },
    });
    if (product.slug && isUuid(routeKey) && product.id === routeKey) {
      navigate(productPath(product), { replace: true });
    }
    // Nel negozio si vedono solo i prodotti della bottega: gli altri si aprono nel marketplace
    if (shop && product.crafter_id !== shop.crafter.id) {
      exitShop();
      navigate(`/prodotto/${product.slug || product.id}`, { replace: true });
    }
  }, [product, crafter, routeKey, navigate]);
  useEffect(() => () => setJsonLd('product', null), []);

  // Risolve slug -> id (retrocompat con vecchi URL UUID)
  useEffect(() => {
    let active = true;
    if (isUuid(routeKey)) {
      setId(routeKey);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('products')
        .select('id')
        .eq('slug', routeKey)
        .maybeSingle();
      if (active && data) setId(data.id);
    })();
    return () => { active = false; };
  }, [routeKey]);

  useEffect(() => {
    if (id) {
      loadProductData();
      loadReviewStats();
    }
  }, [id]);

  // Load shipping info when product data is available
  useEffect(() => {
    if (product?.crafter_id) {
      loadShippingInfo();
    }
  }, [product?.crafter_id]);

  // Load product highlights details
  useEffect(() => {
    const loadHighlights = async () => {
      if (product?.highlights && product.highlights.length > 0) {
        try {
          const keys = product.highlights.join(',');
          const response = await fetch(`${API_URL}/api/product-highlights/by-keys?keys=${encodeURIComponent(keys)}`);
          if (response.ok) {
            const data = await response.json();
            setProductHighlights(data.highlights || []);
          }
        } catch (error) {
          console.error('Error loading highlights:', error);
        }
      } else {
        setProductHighlights([]);
      }
    };
    
    loadHighlights();
  }, [product?.highlights, API_URL]);

  const loadShippingInfo = async () => {
    if (!product?.crafter_id) return;
    
    setLoadingShipping(true);
    try {
      const response = await fetch(`${API_URL}/api/shipping-rules/${product.crafter_id}`);
      if (response.ok) {
        const data = await response.json();
        // Find default active rule
        const defaultRule = data.rules?.find(r => r.is_default && r.is_active);
        if (defaultRule) {
          setShippingInfo(defaultRule);
        } else if (data.rules?.length > 0) {
          // Use first active rule if no default
          const activeRule = data.rules.find(r => r.is_active);
          setShippingInfo(activeRule || null);
        }
      }
    } catch (error) {
      console.error('Error loading shipping info:', error);
      // Fallback default
      setShippingInfo({
        name: 'Spedizione Standard',
        price: 5.00,
        delivery_days_min: 1,
        delivery_days_max: 3,
        free_shipping_threshold: null
      });
    } finally {
      setLoadingShipping(false);
    }
  };

  const loadReviewStats = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('rating')
        .eq('product_id', id);

      if (!error && data && data.length > 0) {
        const total = data.reduce((sum, r) => sum + r.rating, 0);
        setReviewStats({
          count: data.length,
          average: total / data.length
        });
      }
    } catch (error) {
      console.error('Error loading review stats:', error);
    }
  };

  const loadCrafterRating = async (crafterId) => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('rating')
        .eq('crafter_id', crafterId);

      if (!error && data && data.length > 0) {
        const total = data.reduce((sum, r) => sum + r.rating, 0);
        setCrafterRating({
          count: data.length,
          average: total / data.length
        });
      }
    } catch (error) {
      console.error('Error loading crafter rating:', error);
    }
  };

  const loadProductData = async () => {
    setLoading(true);
    try {
      const { data: productData } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();

      if (productData) {
        setProduct(productData);
        const mainIndex = productData.customization_options?.main_image_index || 0;
        setSelectedImageIndex(mainIndex);

        const { data: crafterData } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', productData.crafter_id)
          .single();

        if (crafterData) {
          setCrafter(crafterData);
          // Load crafter's overall rating
          loadCrafterRating(crafterData.id);
        }
      }
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCustomization = (index) => {
    setSelectedCustomizations(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const calculateTotalPrice = () => {
    let netTotal = product?.base_price || 0;
    const customizations = product?.customization_options?.customizations || [];
    const variants = product?.customization_options?.variants || [];
    const crafterDiscount = product?.customization_options?.crafterDiscount || 0;
    
    // Add variant price increases (these are also net prices)
    variants.forEach(variant => {
      const selectedValue = selectedVariants[variant.type] || variant.values[0];
      const priceIncrease = variant.prices?.[selectedValue] || 0;
      netTotal += parseFloat(priceIncrease);
    });
    
    // Add customization price increases (these are also net prices)
    Object.keys(selectedCustomizations).forEach(index => {
      if (selectedCustomizations[index] && customizations[index]?.priceIncrease) {
        netTotal += parseFloat(customizations[index].priceIncrease);
      }
    });
    
    // Apply crafter discount and Rarifex fee to get final price
    return calculateFinalPrice(netTotal, crafterDiscount).toFixed(2);
  };

  const getBasePrice = () => {
    // Return the final price (with fees and crafter discount) for the base product
    const crafterDiscount = product?.customization_options?.crafterDiscount || 0;
    return calculateFinalPrice(product?.base_price || 0, crafterDiscount).toFixed(2);
  };
  
  const getCrafterDiscount = () => {
    return product?.customization_options?.crafterDiscount || 0;
  };

  const getPriceBreakdown = () => {
    const breakdown = [];
    const variants = product?.customization_options?.variants || [];
    const customizations = product?.customization_options?.customizations || [];
    
    // Variant extras
    variants.forEach(variant => {
      const selectedValue = selectedVariants[variant.type] || variant.values[0];
      const priceIncrease = variant.prices?.[selectedValue] || 0;
      if (priceIncrease > 0) {
        breakdown.push({ label: `${variant.type}: ${selectedValue}`, price: priceIncrease });
      }
    });
    
    // Customization extras
    Object.keys(selectedCustomizations).forEach(index => {
      if (selectedCustomizations[index] && customizations[index]?.priceIncrease > 0) {
        breakdown.push({ label: customizations[index].name, price: customizations[index].priceIncrease });
      }
    });
    
    return breakdown;
  };

  const calculateTotalTimeline = () => {
    // Get current variant settings (uses hooks computed values)
    const variantSettings = product?.customization_options?.variantSettings || {};
    const legacyTimeline = product?.customization_options?.timeline || 'immediate';
    const variants = product?.customization_options?.variants || [];
    const hasVariants = product?.customization_options?.hasVariants && variants.length > 0;
    
    // Get current variant key
    const variantKey = hasVariants 
      ? variants.map(v => `${v.type}:${selectedVariants[v.type] || v.values[0]}`).join('|')
      : 'default';
    
    // Get timeline for current variant
    const settings = variantSettings[variantKey];
    const baseTimeline = settings?.timeline || legacyTimeline;
    
    const customizations = product?.customization_options?.customizations || [];
    
    // Find the longest timeline selected
    let maxTimeline = baseTimeline;
    const timelineOrder = ['immediate', '1-2days', '3-5days', '1week', '1-2weeks', '3-4weeks', 'over-month'];
    
    Object.keys(selectedCustomizations).forEach(index => {
      if (selectedCustomizations[index] && customizations[index]?.timeline) {
        const customTimeline = customizations[index].timeline;
        if (timelineOrder.indexOf(customTimeline) > timelineOrder.indexOf(maxTimeline)) {
          maxTimeline = customTimeline;
        }
      }
    });
    
    return TIMELINE_LABELS[maxTimeline] || maxTimeline;
  };

  const handleFileUpload = async (customIndex, file) => {
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File troppo grande (max 10MB)');
      return;
    }

    setUploadingAttachment(customIndex);
    setUploadErrors(prev => ({ ...prev, [customIndex]: null }));
    
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `customer-attachment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      const filePath = `customer-attachments/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('rarifex-uploads')
        .upload(filePath, file);
      
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage
        .from('rarifex-uploads')
        .getPublicUrl(filePath);
      
      setCustomerAttachments(prev => ({
        ...prev,
        [customIndex]: { url: publicUrl, name: file.name }
      }));
      
      toast.success('File caricato!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Errore nel caricamento del file');
      setUploadErrors(prev => ({
        ...prev,
        [customIndex]: 'Non siamo riusciti a caricare il file. Riprova; se il problema continua, scrivi all\'artigiano dalla chat.'
      }));
    } finally {
      setUploadingAttachment(null);
    }
  };

  const removeAttachment = (customIndex) => {
    setCustomerAttachments(prev => {
      const newAttachments = { ...prev };
      delete newAttachments[customIndex];
      return newAttachments;
    });
  };

  const handleContactCrafter = async () => {
    if (!user) {
      toast.error('Accedi per contattare l\'artigiano');
      navigate('/login');
      return;
    }

    // Navigate to messages - could pre-fill with product info
    navigate(`/messages?crafter=${crafter.id}&product=${product.id}`);
  };

  const handleAddToCart = () => {
    if (!user) {
      toast.error('Accedi per aggiungere al carrello');
      navigate('/login');
      return;
    }

    // Check if crafter data is loaded
    if (!crafter?.id) {
      toast.error('Errore nel caricamento del prodotto. Ricarica la pagina.');
      return;
    }

    // Blocco acquisto se l'artigiano è in ferie
    const vacCheck = vacationStatus(crafter);
    if (vacCheck.active) {
      toast.error(`L'artigiano è in ferie fino al ${formatDateIt(vacCheck.end)}. Gli ordini riprenderanno al rientro.`);
      return;
    }

    // Check if product is available
    if (isImmediateAvailability && currentStock === 0) {
      toast.error('Prodotto momentaneamente non disponibile');
      return;
    }

    // Check if all required customizations are filled AND selected customizations have data
    const customizations = product?.customization_options?.customizations || [];
    const missingRequired = [];
    const missingData = [];
    
    customizations.forEach((custom, index) => {
      const isSelected = selectedCustomizations[index];
      const inputType = custom.inputType || 'text';
      
      // Check if required customization is not selected
      if (custom.required && !isSelected) {
        missingRequired.push(custom.name);
        return;
      }
      
      // If customization is selected, verify the data is provided
      if (isSelected) {
        if (inputType === 'file') {
          // File upload must have attachment
          if (!customerAttachments[index]) {
            missingData.push(`Carica il file per "${custom.name}"`);
          }
        } else if (inputType === 'select') {
          // Select must have a value chosen
          if (!customizationValues[index]) {
            missingData.push(`Seleziona un'opzione per "${custom.name}"`);
          }
        } else if (inputType === 'text' || inputType === 'textarea') {
          // Text/textarea must have content
          if (!customizationValues[index] || customizationValues[index].trim() === '') {
            missingData.push(`Inserisci il testo per "${custom.name}"`);
          }
        }
      }
    });

    if (missingRequired.length > 0) {
      toast.error(`Seleziona le personalizzazioni obbligatorie: ${missingRequired.join(', ')}`);
      return;
    }
    
    if (missingData.length > 0) {
      toast.error(missingData[0]); // Show first missing data error
      return;
    }

    // Build cart item
    const cartItem = {
      productId: product.id,
      title: product.title,
      image: product.images?.[0],
      basePrice: parseFloat(getBasePrice()), // Final price with fees
      totalPrice: parseFloat(calculateTotalPrice()), // Final price with fees + extras
      quantity: 1,
      fulfillmentType: product.fulfillment_type || 'STANDARD',
      selectedVariants: { ...selectedVariants },
      selectedCustomizations: { ...selectedCustomizations },
      customizationValues: { ...customizationValues },
      customerAttachments: { ...customerAttachments },
      crafterId: crafter?.id,
      crafterName: crafter?.storefront_title || crafter?.full_name,
      crafter: {
        id: crafter?.id,
        name: crafter?.storefront_title || crafter?.full_name
      }
    };

    // Get current cart from localStorage
    const currentCart = JSON.parse(localStorage.getItem('rarifex_cart') || '[]');
    
    // Add item to cart
    currentCart.push(cartItem);
    localStorage.setItem('rarifex_cart', JSON.stringify(currentCart));
    
    // Dispatch event to update cart badge
    window.dispatchEvent(new Event('cartUpdated'));

    // Mostra dialog animato di conferma (sostituisce il vecchio toast)
    setAddedDialog({
      open: true,
      item: cartItem,
      cartCount: currentCart.reduce((acc, i) => acc + (Number(i.quantity) || 1), 0),
    });
  };

  const nextImage = () => {
    if (product?.images?.length > 1) {
      setSelectedImageIndex((prev) => (prev + 1) % product.images.length);
    }
  };

  const prevImage = () => {
    if (product?.images?.length > 1) {
      setSelectedImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shop-primary"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Prodotto non trovato</h2>
          <Link to="/esplora">
            <Button className="bg-shop-primary text-white">Esplora Creazioni</Button>
          </Link>
        </div>
      </div>
    );
  }

  const customizations = product.customization_options?.customizations || [];
  const variants = product.customization_options?.variants || [];
  const hasVariants = product.customization_options?.hasVariants && variants.length > 0;
  const hasCustomizationsEnabled = product.customization_options?.hasCustomizations && customizations.length > 0;
  const variantSettings = product.customization_options?.variantSettings || {};
  const legacyTimeline = product.customization_options?.timeline || 'immediate';
  const legacyInventory = product.customization_options?.inventory || {};
  const hasCustomizationsSelected = Object.values(selectedCustomizations).some(v => v);

  // Get current variant key
  const getInventoryKey = () => {
    if (!hasVariants) return 'default';
    return variants.map(v => `${v.type}:${selectedVariants[v.type] || v.values[0]}`).join('|');
  };
  
  const currentVariantKey = getInventoryKey();
  
  // Get availability for current variant (check variantSettings first, then fallback to legacy)
  const getCurrentVariantSettings = () => {
    const settings = variantSettings[currentVariantKey];
    if (settings) {
      return {
        timeline: settings.timeline || legacyTimeline,
        stock: settings.stock ?? legacyInventory[currentVariantKey] ?? 0
      };
    }
    // Fallback to legacy data
    return {
      timeline: legacyTimeline,
      stock: legacyInventory[currentVariantKey] ?? 0
    };
  };
  
  const currentSettings = getCurrentVariantSettings();
  const currentStock = currentSettings.stock;
  const isImmediateAvailability = currentSettings.timeline === 'immediate';
  const currentTimeline = currentSettings.timeline;

  return (
    <div className={`min-h-screen pt-4 ${shop ? '' : 'bg-white'}`} data-testid="product-detail-page">
      {/* Breadcrumb */}
      {shop ? (
        <div className={`${shop ? 'max-w-6xl' : 'max-w-7xl'} mx-auto px-4 pt-4`}>
          <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="shop-product-breadcrumb">
            <Link to={shop.paths.home} className="hover:text-shop-primary">{shop.name}</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to={shop.paths.page('prodotti')} className="hover:text-shop-primary">Creazioni</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-800 truncate max-w-[200px]">{product.title}</span>
          </div>
        </div>
      ) : (
      <div className="bg-slate-50 border-b">
        <div className={`${shop ? 'max-w-6xl' : 'max-w-7xl'} mx-auto px-4 py-3`}>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/products" className="hover:text-shop-primary">Prodotti</Link>
            {(() => {
              // Get category path from customization_options (new format)
              const categoryPath = product.customization_options?.categoryPath;
              
              if (categoryPath && categoryPath.main && categoryPath.mainName && categoryPath.sub && categoryPath.child) {
                // Full category path available
                return (
                  <>
                    <ChevronRight className="w-4 h-4" />
                    <Link 
                      to={`/products?category=${categoryPath.main}`} 
                      className="hover:text-shop-primary"
                    >
                      {categoryPath.mainName}
                    </Link>
                    <ChevronRight className="w-4 h-4" />
                    <Link 
                      to={`/products?category=${categoryPath.main}&sub=${categoryPath.sub.toLowerCase().replace(/ /g, '-')}`} 
                      className="hover:text-shop-primary"
                    >
                      {categoryPath.sub}
                    </Link>
                    <ChevronRight className="w-4 h-4" />
                    <span className="text-slate-700 font-medium">
                      {getChildCategoryName(categoryPath)}
                    </span>
                  </>
                );
              } else if (product.category) {
                // Fallback: only main category ID available (old format)
                // Show the category ID as-is since we don't have static fallback anymore
                return (
                  <>
                    <ChevronRight className="w-4 h-4" />
                    <Link 
                      to={`/products?category=${product.category}`} 
                      className="hover:text-shop-primary"
                    >
                      {product.category}
                    </Link>
                  </>
                );
              }
              return null;
            })()}
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-800 truncate max-w-[200px]">{product.title}</span>
          </div>
        </div>
      </div>
      )}

      <div className={`${shop ? 'max-w-6xl' : 'max-w-7xl'} mx-auto px-4 py-8`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          
          {/* Left Column - Images */}
          <div className="lg:col-span-7">
            {/* Main Image */}
            <div 
              className="relative aspect-square bg-white rounded-2xl overflow-hidden mb-4 group cursor-zoom-in border border-slate-100"
              onClick={() => setIsZoomOpen(true)}
            >
              <AnimatePresence mode="wait">
                <motion.img
                  key={selectedImageIndex}
                  src={optimizeImageUrl(product.images?.[selectedImageIndex], 1000)}
                  alt={product.title}
                  className="w-full h-full object-contain"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                />
              </AnimatePresence>
              
              {/* Zoom indicator */}
              <div className="absolute bottom-4 right-4 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <ZoomIn className="w-3.5 h-3.5" />
                Clicca per ingrandire
              </div>
              
              {/* Navigation */}
              {product.images?.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:scale-105"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:scale-105"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}

              {/* Image counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
                {selectedImageIndex + 1} / {product.images?.length || 1}
              </div>

              {/* Favorite button */}
              <button 
                onClick={(e) => { e.stopPropagation(); handleToggleFavorite(); }}
                className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:scale-105 transition-transform"
                data-testid="favorite-product-btn"
              >
                <Heart className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-slate-400'}`} />
              </button>
            </div>

            {/* Image Zoom Modal */}
            <Dialog open={isZoomOpen} onOpenChange={setIsZoomOpen}>
              <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none">
                <div className="relative w-full h-[90vh] flex items-center justify-center">
                  {/* Close button */}
                  <button
                    onClick={() => setIsZoomOpen(false)}
                    className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                  
                  {/* Navigation in zoom */}
                  {product.images?.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                      >
                        <ChevronLeft className="w-8 h-8" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                      >
                        <ChevronRight className="w-8 h-8" />
                      </button>
                    </>
                  )}
                  
                  {/* Zoomed image */}
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={selectedImageIndex}
                      src={optimizeImageUrl(product.images?.[selectedImageIndex], 1000)}
                      alt={product.title}
                      className="max-w-full max-h-full object-contain"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.2 }}
                    />
                  </AnimatePresence>
                  
                  {/* Image counter in zoom */}
                  <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/20 text-white text-sm px-4 py-2 rounded-full backdrop-blur-sm">
                    {selectedImageIndex + 1} / {product.images?.length || 1}
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Thumbnails */}
            {product.images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                      selectedImageIndex === index 
                        ? 'border-shop-primary ring-2 ring-shop-primary/20' 
                        : 'border-white hover:border-slate-200 shadow-sm'
                    }`}
                  >
                    <img src={optimizeImageUrl(img, 160)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Product Description - Desktop */}
            <div className="hidden lg:block mt-8">
              <h3 className="text-lg font-semibold mb-3">Descrizione</h3>
              <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">
                {product.description || 'Nessuna descrizione disponibile.'}
              </p>
            </div>

            {/* Crafter Info - Desktop */}
            {crafter && (
              <div className="hidden lg:block mt-8">
                <h3 className="text-lg font-semibold mb-3">L'artigiano</h3>
                <Link to={crafterUrl(crafter)}>
                  <Card className="p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-shop-primary flex-shrink-0">
                        {crafter.avatar_url ? (
                          <img src={optimizeImageUrl(crafter.avatar_url, 96)} alt={crafter.full_name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white font-bold text-2xl">
                            {(crafter.storefront_title || crafter.full_name || 'A')[0].toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-lg">{crafter.storefront_title || crafter.full_name}</span>
                          {crafter.tier === 'pro' && (
                            <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs">PRO</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          {crafterRating.count > 0 ? (
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                              <span className="font-medium text-slate-700">{crafterRating.average.toFixed(1)}</span>
                              <span className="text-xs text-slate-400">({crafterRating.count})</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-slate-400">
                              <Star className="w-4 h-4" />
                              <span className="text-xs">Nuovo</span>
                            </div>
                          )}
                          {crafter.city && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              <span>{crafter.city}</span>
                            </div>
                          )}
                          {crafter.years_experience && (
                            <div className="flex items-center gap-1">
                              <Award className="w-4 h-4" />
                              <span>{crafter.years_experience} anni exp.</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </Card>
                </Link>
              </div>
            )}
          </div>

          {/* Right Column - Product Info & Actions */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              {/* Title */}
              <div className="mb-4">
                {customizations.length > 0 && (
                  <div className="mb-2">
                    <Badge className="bg-shop-accent/10 text-shop-accent border border-shop-accent/30 text-xs">
                      Personalizzabile
                    </Badge>
                  </div>
                )}
                <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
                  {product.title}
                </h1>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-4">
                <a 
                  href="#reviews" 
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('reviews')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {reviewStats.count > 0 ? (
                    <>
                      <ReviewStars rating={reviewStats.average} size="sm" />
                      <span className="text-sm font-medium">{reviewStats.average.toFixed(1)}</span>
                      <span className="text-sm text-shop-primary underline">
                        ({reviewStats.count} recension{reviewStats.count === 1 ? 'e' : 'i'})
                      </span>
                    </>
                  ) : (
                    <>
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map(i => (
                          <Star key={i} className="w-4 h-4 text-slate-200" />
                        ))}
                      </div>
                      <span className="text-sm text-slate-500">Nessuna recensione</span>
                    </>
                  )}
                </a>
              </div>

              {/* Price */}
              <div className="mb-6">
                {(() => {
                  const crafterDiscount = getCrafterDiscount();
                  const launchModeActive = isLaunchModeActive();
                  const hasDiscount = crafterDiscount > 0;
                  const originalPrice = calculateFullPrice(product?.base_price || 0);
                  const totalDiscountPercent = getTotalDiscountPercentage(crafterDiscount);
                  
                  return (
                    <>
                      {hasDiscount && (
                        <div className="mb-2 flex items-center gap-2 flex-wrap">
                          {crafterDiscount > 0 && (
                            <Badge className="bg-orange-500 text-white text-xs px-2 py-1">
                              🏷️ Sconto Artigiano -{crafterDiscount}%
                            </Badge>
                          )}
                        </div>
                      )}
                      <div className="flex items-baseline gap-3">
                        <span className="text-3xl font-bold text-slate-900">€{calculateTotalPrice()}</span>
                        {hasDiscount && (
                          <span className="text-lg text-slate-400 line-through">
                            €{originalPrice.toFixed(2)}
                          </span>
                        )}
                        {/* Senza sconto non si barra nulla: i supplementi sono già dettagliati sotto. */}
                      </div>
                    </>
                  );
                })()}
                {/* Price breakdown */}
                {getPriceBreakdown().length > 0 && (
                  <div className="mt-2 text-sm text-slate-600">
                    <span className="text-slate-400">Base €{getBasePrice()}</span>
                    {getPriceBreakdown().map((item, idx) => (
                      <span key={idx} className="text-green-600 ml-2">+€{item.price} ({item.label})</span>
                    ))}
                  </div>
                )}
                <p className="text-sm text-slate-500 mt-1">IVA inclusa dove applicabile</p>
              </div>

              {/* Timeline & Stock */}
              <div className={`p-3 rounded-lg mb-6 ${
                isImmediateAvailability && currentStock === 0 ? 'bg-red-50' : 'bg-slate-50'
              }`}>
                <div className="flex items-center gap-2">
                  <Clock className={`w-5 h-5 ${
                    isImmediateAvailability && currentStock === 0 ? 'text-red-500' : 'text-shop-primary'
                  }`} />
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {isImmediateAvailability 
                        ? (currentStock === 0 ? 'Disponibilità' : 'Disponibilità')
                        : 'Tempo di realizzazione'
                      }
                    </p>
                    <p className={`text-sm ${
                      isImmediateAvailability && currentStock === 0 ? 'text-red-600 font-medium' : 'text-slate-600'
                    }`}>
                      {isImmediateAvailability && currentStock === 0 
                        ? 'Prodotto momentaneamente non disponibile'
                        : calculateTotalTimeline()
                      }
                    </p>
                  </div>
                  {isImmediateAvailability && currentStock > 0 && (
                    <Badge className="bg-green-100 text-green-700">
                      {currentStock} disponibili
                    </Badge>
                  )}
                </div>
                {/* Info text for "su commissione" products */}
                {!isImmediateAvailability && (() => {
                  const hasMandatoryCustomization = (product?.customization_options?.customizations || [])
                    .some(c => c?.required === true);
                  return (
                    <p className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-200">
                      Questo articolo verrà realizzato appositamente per te, come da foto e descrizione.
                      {hasMandatoryCustomization
                        ? ' Si applicano i tempi di realizzazione indicati.'
                        : ' Si applicano i tempi di realizzazione indicati e i diritti standard di recesso e reso.'}
                    </p>
                  );
                })()}
              </div>

              {/* Shipping Calculator Box */}
              {shippingInfo && (
                <div className="mb-6 p-4 bg-gradient-to-r from-shop-primary/5 to-shop-primary/10 rounded-xl border border-shop-primary/20">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-shop-primary flex items-center justify-center flex-shrink-0">
                      <Truck className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-shop-primary">Spedizione in Italia</h4>
                        {loadingShipping ? (
                          <span className="text-sm text-slate-500">Caricamento...</span>
                        ) : shippingInfo.price === 0 ? (
                          <span className="font-bold text-green-600">Gratis</span>
                        ) : (
                          <span className="font-bold text-shop-primary">€{shippingInfo.price?.toFixed(2)}</span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Clock className="w-4 h-4" />
                        <span>
                          {shippingInfo.delivery_days_min === shippingInfo.delivery_days_max 
                            ? `${shippingInfo.delivery_days_min} giorno lavorativo`
                            : `${shippingInfo.delivery_days_min}-${shippingInfo.delivery_days_max} giorni lavorativi`
                          }
                        </span>
                      </div>
                      
                      {shippingInfo.free_shipping_threshold && shippingInfo.price > 0 && (
                        <div className="mt-2 text-sm">
                          {(() => {
                            const currentPrice = calculateTotalPrice();
                            const threshold = shippingInfo.free_shipping_threshold;
                            const remaining = threshold - currentPrice;
                            
                            if (remaining <= 0) {
                              return (
                                <span className="text-green-600 font-medium flex items-center gap-1">
                                  <Check className="w-4 h-4" />
                                  Spedizione gratuita applicata!
                                </span>
                              );
                            } else {
                              return (
                                <span className="text-slate-500">
                                  Aggiungi <span className="font-semibold text-shop-primary">€{remaining.toFixed(2)}</span> per la spedizione gratuita
                                </span>
                              );
                            }
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Mobile Description */}
              <div className="lg:hidden mb-6">
                <p className="text-slate-600">{product.description}</p>
              </div>

              {/* Variants Selection */}
              {hasVariants && (
                <div className="mb-6">
                  <h3 className="font-semibold text-lg mb-3">Seleziona variante</h3>
                  <div className="space-y-4">
                    {variants.map((variant, vIndex) => (
                      <div key={vIndex}>
                        <Label className="text-sm font-medium text-slate-700 mb-2 block">
                          {variant.type}
                        </Label>
                        <div className="flex flex-wrap gap-2">
                          {variant.values.map((value, valIndex) => {
                            const priceExtra = variant.prices?.[value] || 0;
                            const isSelected = (selectedVariants[variant.type] || variant.values[0]) === value;
                            const variantImageUrl = variant.images?.[value];
                            return (
                              <button
                                key={valIndex}
                                type="button"
                                onClick={() => {
                                  setSelectedVariants(prev => ({ ...prev, [variant.type]: value }));
                                  // Se questo valore ha un'immagine associata, scorri il carosello
                                  if (variantImageUrl && product?.images?.length > 0) {
                                    const idx = product.images.indexOf(variantImageUrl);
                                    if (idx >= 0) setSelectedImageIndex(idx);
                                  }
                                }}
                                className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                                  isSelected
                                    ? 'border-shop-primary bg-shop-primary/5 text-shop-primary'
                                    : 'border-slate-200 hover:border-slate-300 text-slate-700'
                                }`}
                                data-testid={`variant-btn-${variant.type}-${value}`}
                              >
                                {value}
                                {priceExtra > 0 && (
                                  <span className={`ml-1.5 ${isSelected ? 'text-shop-primary-dark' : 'text-green-600'}`}>
                                    +€{priceExtra}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Customizations */}
              {hasCustomizationsEnabled && (
                <div className="mb-6">
                  <h3 className="font-semibold text-lg mb-3">Personalizzazioni disponibili</h3>
                  <div className="space-y-4">
                    {customizations.map((custom, index) => {
                      const isSelected = selectedCustomizations[index];
                      const inputType = custom.inputType || 'text';
                      
                      return (
                        <div key={index} className="border rounded-xl overflow-hidden">
                          {/* Customization Header */}
                          <button
                            type="button"
                            onClick={() => toggleCustomization(index)}
                            className={`w-full p-4 text-left transition-all ${
                              isSelected
                                ? 'bg-shop-primary/5 border-l-4 border-l-shop-primary'
                                : 'hover:bg-slate-50'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                isSelected
                                  ? 'border-shop-primary bg-shop-primary'
                                  : 'border-slate-300'
                              }`}>
                                {isSelected && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-medium">{custom.name}</span>
                                  <div className="flex items-center gap-2">
                                    {custom.required && (
                                      <Badge variant="outline" className="text-xs border-amber-400 text-amber-600">
                                        Obbligatoria
                                      </Badge>
                                    )}
                                    {custom.priceIncrease > 0 && (
                                      <span className="text-shop-primary font-semibold whitespace-nowrap">
                                        +€{custom.priceIncrease}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <p className="text-xs text-slate-500 mt-1">
                                  {inputType === 'text' && 'Inserisci un testo'}
                                  {inputType === 'textarea' && 'Scrivi una descrizione'}
                                  {inputType === 'file' && 'Carica un file'}
                                  {inputType === 'select' && 'Scegli un\'opzione'}
                                </p>
                              </div>
                            </div>
                          </button>

                          {/* Customization Input (when selected) */}
                          {isSelected && (
                            <div className="p-4 bg-slate-50 border-t">
                              {inputType === 'text' && (
                                <div>
                                  <Label className="text-sm">Inserisci il testo</Label>
                                  <Input
                                    value={customizationValues[index] || ''}
                                    onChange={(e) => setCustomizationValues(prev => ({
                                      ...prev,
                                      [index]: e.target.value
                                    }))}
                                    placeholder="Es: Nome da incidere..."
                                    className="mt-1"
                                  />
                                </div>
                              )}
                              
                              {inputType === 'textarea' && (
                                <div>
                                  <Label className="text-sm">Descrivi la personalizzazione</Label>
                                  <Textarea
                                    value={customizationValues[index] || ''}
                                    onChange={(e) => setCustomizationValues(prev => ({
                                      ...prev,
                                      [index]: e.target.value
                                    }))}
                                    placeholder="Descrivi in dettaglio..."
                                    rows={3}
                                    className="mt-1"
                                  />
                                </div>
                              )}
                              
                              {inputType === 'select' && custom.options?.length > 0 && (
                                <div>
                                  <Label className="text-sm">Seleziona un'opzione</Label>
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {custom.options.map((opt, optIdx) => (
                                      <button
                                        key={optIdx}
                                        type="button"
                                        onClick={() => setCustomizationValues(prev => ({
                                          ...prev,
                                          [index]: opt
                                        }))}
                                        className={`px-3 py-1.5 rounded-lg border text-sm transition-all ${
                                          customizationValues[index] === opt
                                            ? 'border-shop-primary bg-shop-primary text-white'
                                            : 'border-slate-200 hover:border-shop-primary text-slate-700'
                                        }`}
                                      >
                                        {opt}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {inputType === 'file' && (
                                <div>
                                  <Label className="text-sm mb-2 block">Carica il tuo file</Label>
                                  {customerAttachments[index] ? (
                                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                                      {customerAttachments[index].url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                        <img 
                                          src={customerAttachments[index].url} 
                                          alt="" 
                                          className="w-16 h-16 rounded object-cover"
                                        />
                                      ) : (
                                        <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center">
                                          <ImageIcon className="w-8 h-8 text-slate-400" />
                                        </div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium truncate">{customerAttachments[index].name}</p>
                                        <p className="text-xs text-green-600 flex items-center gap-1">
                                          <Check className="w-3 h-3" />
                                          Caricato
                                        </p>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => removeAttachment(index)}
                                        className="p-2 hover:bg-red-50 rounded-full text-red-500"
                                      >
                                        <X className="w-5 h-5" />
                                      </button>
                                    </div>
                                  ) : (
                                    <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-100/50 transition-colors">
                                      <input
                                        type="file"
                                        className="hidden"
                                        accept="image/*,.pdf"
                                        onChange={(e) => handleFileUpload(index, e.target.files?.[0])}
                                        disabled={uploadingAttachment === index}
                                      />
                                      {uploadingAttachment === index ? (
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-shop-primary"></div>
                                      ) : (
                                        <>
                                          <Upload className="w-8 h-8 text-slate-400 mb-2" />
                                          <span className="text-sm font-medium text-slate-600">Clicca per caricare</span>
                                          <span className="text-xs text-slate-400 mt-1">JPG, PNG, PDF (max 10MB)</span>
                                        </>
                                      )}
                                    </label>
                                  )}
                                  {uploadErrors[index] && !customerAttachments[index] && (
                                    <p className="mt-2 text-sm text-red-600" role="alert" data-testid={`customization-upload-error-${index}`}>
                                      {uploadErrors[index]}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Withdrawal Rights Warning for Customizable Products */}
              {hasCustomizationsEnabled && hasCustomizationsSelected && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="withdrawal-warning">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold text-amber-800 mb-1">Prodotto su misura</p>
                      <p className="text-amber-700">
                        Selezionando una personalizzazione, il prodotto diventa <strong>su misura</strong>. 
                        Il diritto di recesso è escluso ai sensi dell'art. 59 del Codice del Consumo, 
                        salvo prodotto difettoso, non conforme o diverso da quanto promesso.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                {/* Banner: artigiano in ferie */}
                {vacationStatus(crafter).active && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="product-vacation-banner">
                    <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-semibold">Artigiano in ferie fino al {formatDateIt(vacationStatus(crafter).end)}</p>
                      <p>Gli ordini riprenderanno al rientro.{vacationStatus(crafter).message ? ` ${vacationStatus(crafter).message}` : ''}</p>
                    </div>
                  </div>
                )}
                {/* Banner: artigiano non ancora attivato per la vendita */}
                {!canSellLoading && crafterCanSell === false && (
                  <CrafterStripeStatusBanner
                    variant="client"
                    crafterName={crafter?.storefront_title || crafter?.full_name || 'L\'artigiano'}
                  />
                )}
                <Button 
                  onClick={handleAddToCart}
                  className="w-full bg-shop-primary hover:bg-shop-primary-dark text-white py-6 text-lg font-semibold"
                  disabled={
                    (isImmediateAvailability && currentStock === 0) ||
                    crafterCanSell === false ||
                    vacationStatus(crafter).active
                  }
                  data-testid="product-detail-buy-btn"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  {vacationStatus(crafter).active
                    ? 'Artigiano in ferie'
                    : (crafterCanSell === false
                      ? 'Artigiano non ancora attivo'
                      : (isImmediateAvailability && currentStock === 0
                          ? 'Non disponibile'
                          : 'Aggiungi al carrello'))}
                </Button>
                <Button 
                  onClick={handleContactCrafter}
                  variant="outline"
                  className="w-full py-5"
                >
                  <MessageSquare className="w-5 h-5 mr-2" />
                  Contatta l&apos;artigiano
                </Button>
              </div>

              {/* Star Seller Badge - Shows only if rating >= 4.8 */}
              {crafterRating.count > 0 && crafterRating.average >= 4.8 && crafter && (
                <Link to={crafterUrl(crafter)} className="block mt-4">
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-4 hover:shadow-md transition-all">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-sm">
                        <Award className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-amber-900">Artigiano Stellato</span>
                          <div className="flex items-center gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                className={`w-3.5 h-3.5 ${i < Math.round(crafterRating.average) ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} 
                              />
                            ))}
                          </div>
                        </div>
                        <p className="text-xs text-amber-700 mt-0.5">
                          Valutazione media {crafterRating.average.toFixed(1)} su {crafterRating.count} {crafterRating.count === 1 ? 'recensione' : 'recensioni'}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-amber-400" />
                    </div>
                  </div>
                </Link>
              )}

              {/* Punti di Forza - Product Highlights */}
              {productHighlights.length > 0 && (
                <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-shop-primary" />
                    <span className="font-semibold text-slate-800">Punti di Forza</span>
                  </div>
                  <div className="space-y-2">
                    {productHighlights.map((highlight) => {
                      const IconComp = getHighlightIcon(highlight.icon);
                      return (
                        <div 
                          key={highlight.key}
                          className="flex items-center gap-2.5 text-sm"
                          title={highlight.description}
                        >
                          <div className="w-6 h-6 rounded-full bg-shop-primary/10 flex items-center justify-center flex-shrink-0">
                            <IconComp className="w-3.5 h-3.5 text-shop-primary" />
                          </div>
                          <span className="text-slate-700">{highlight.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Trust Badges */}
              <div className="mt-6 pt-6 border-t">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-start gap-2">
                    <Shield className="w-5 h-5 text-shop-primary flex-shrink-0" />
                    <div>
                      <p className="font-medium">Pagamento sicuro</p>
                      <p className="text-xs text-slate-500">Transazioni protette</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Truck className="w-5 h-5 text-shop-primary flex-shrink-0" />
                    <div>
                      <p className="font-medium">Spedizione tracciata</p>
                      <p className="text-xs text-slate-500">Consegna monitorata</p>
                    </div>
                  </div>
                  {hasCustomizationsEnabled ? (
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-5 h-5 text-shop-primary flex-shrink-0" />
                      <div>
                        <p className="font-medium">Prodotto su misura</p>
                        <p className="text-xs text-slate-500">Reso non applicabile per legge</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <RefreshCw className="w-5 h-5 text-shop-primary flex-shrink-0" />
                      <div>
                        <p className="font-medium">Reso facile</p>
                        <p className="text-xs text-slate-500">14 giorni per il reso</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-2">
                    <MessageSquare className="w-5 h-5 text-shop-primary flex-shrink-0" />
                    <div>
                      <p className="font-medium">Supporto diretto</p>
                      <p className="text-xs text-slate-500">Parla con l'artigiano</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Crafter Card */}
              {crafter && (
                <div className="lg:hidden mt-6">
                  <Link to={crafterUrl(crafter)}>
                    <Card className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-shop-primary">
                          {crafter.avatar_url ? (
                            <img src={optimizeImageUrl(crafter.avatar_url, 96)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white font-bold">
                              {(crafter.storefront_title || crafter.full_name || 'A')[0].toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <span className="font-semibold">{crafter.storefront_title || crafter.full_name}</span>
                          {crafterRating.count > 0 ? (
                            <div
                              className="flex items-center gap-1 text-sm text-slate-500"
                              data-testid="mobile-crafter-rating"
                            >
                              <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                              <span className="font-medium text-slate-700">{crafterRating.average.toFixed(1)}</span>
                              <span className="text-xs text-slate-400">({crafterRating.count})</span>
                            </div>
                          ) : (
                            <div
                              className="flex items-center gap-1 text-sm text-slate-400"
                              data-testid="mobile-crafter-new"
                            >
                              <Star className="w-3 h-3" />
                              <span className="text-xs">Nuovo</span>
                            </div>
                          )}
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </Card>
                  </Link>
                </div>
              )}

              {/* Share */}
              <div className="mt-6 flex items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={async () => {
                    const shareUrl = window.location.href;
                    const shareTitle = product?.title || 'Creazione artigianale su Rarifex';
                    const shareText = crafter?.storefront_title || crafter?.full_name
                      ? `Guarda "${shareTitle}" di ${crafter.storefront_title || crafter.full_name} su Rarifex`
                      : `Guarda "${shareTitle}" su Rarifex`;
                    try {
                      // Prova la Web Share API (mobile + Safari desktop)
                      if (navigator.share) {
                        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
                        return;
                      }
                      // Fallback: copia link negli appunti
                      if (navigator.clipboard?.writeText) {
                        await navigator.clipboard.writeText(shareUrl);
                        toast.success('Link copiato negli appunti');
                        return;
                      }
                      // Ultimo fallback: apri window.open (raro nei browser moderni)
                      window.prompt('Copia il link:', shareUrl);
                    } catch (err) {
                      // Utente ha annullato lo share nativo → nessun toast
                      if (err?.name !== 'AbortError') {
                        toast.error('Impossibile condividere in questo momento');
                      }
                    }
                  }}
                  className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                  data-testid="share-product-button"
                >
                  <Share2 className="w-4 h-4" />
                  Condividi
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div id="reviews" className="max-w-5xl mx-auto px-4 py-12 scroll-mt-24">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Star className="w-6 h-6 text-amber-500" />
            Recensioni
            {reviewStats.count > 0 && (
              <span className="text-sm font-normal text-slate-500 ml-2">
                ({reviewStats.count})
              </span>
            )}
          </h2>
          <ReviewsList productId={id} limit={10} />
        </div>

        {/* Prodotti consigliati: nel negozio solo creazioni della stessa bottega */}
        {shop ? (
          <ShopProductExtras product={product} />
        ) : (
          <div className="max-w-5xl mx-auto px-4 pb-16">
            <RecommendedProducts 
              context="product"
              productId={id}
              limit={8}
              variant="default"
              title="Potrebbe piacerti anche"
              className="pt-8 border-t border-slate-200"
            />
          </div>
        )}
      </div>

      {/* Dialog conferma aggiunta al carrello */}
      <AddedToCartDialog
        open={addedDialog.open}
        onClose={() => setAddedDialog((d) => ({ ...d, open: false }))}
        product={addedDialog.item}
        cartCount={addedDialog.cartCount}
      />
    </div>
  );
}
